/**
 * デバッグ表示（§6）。開発の速度をそのまま決める層なので、
 * ここは機能追加より先に厚くしておく。
 */
import { TILE, colOf, rowOf } from '../sim/grid';
import { EdgeKind, JumpGraph, NodeKey } from '../sim/graph';
import { CatState, GameState } from '../sim/state';

export const EDGE_COLOR: Record<EdgeKind, string> = {
  walk: '#5fd3bd',
  up: '#f0c069',
  fall: '#7aa2f7',
  // キャットタワーの段づたい（§3.2 の例外）
  climb: '#e884c4',
};

export interface OverlayOptions {
  showEdges: boolean;
  showReach: boolean;
  showNodes: boolean;
  showTraffic: boolean;
  kinds: Record<EdgeKind, boolean>;
}

function center(state: GameState, key: NodeKey): { x: number; y: number } {
  return {
    x: colOf(state.grid, key) * TILE + TILE / 2,
    y: (rowOf(state.grid, key) + 1) * TILE - 3,
  };
}

export function drawGraph(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  graph: JumpGraph,
  reach: Map<NodeKey, NodeKey | null>,
  opt: OverlayOptions,
): void {
  if (opt.showReach) {
    ctx.fillStyle = 'rgba(95,211,189,.30)';
    for (const k of reach.keys()) {
      const p = center(state, k);
      ctx.fillRect(p.x - 1.5, p.y - 1, 3, 3);
    }
  }

  if (opt.showNodes) {
    ctx.strokeStyle = 'rgba(109,121,134,.45)';
    ctx.lineWidth = 1;
    for (const k of graph.nodes.keys()) {
      const p = center(state, k);
      ctx.strokeRect(p.x - 2.5, p.y - 2.5, 5, 5);
    }
  }

  if (opt.showEdges) {
    ctx.lineWidth = 1;
    for (const [from, edges] of graph.edges) {
      const a = center(state, from);
      for (const e of edges) {
        if (!opt.kinds[e.kind]) continue;
        const b = center(state, e.to);
        ctx.strokeStyle = EDGE_COLOR[e.kind] + '55';
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        // 有向であることが見えるように、終点側に矢羽根
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const len = Math.hypot(dx, dy) || 1;
        const ux = dx / len;
        const uy = dy / len;
        ctx.fillStyle = EDGE_COLOR[e.kind] + '99';
        ctx.beginPath();
        ctx.arc(b.x - ux * 3, b.y - uy * 3, 1.4, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
}

/**
 * どのマスが動線になっているか。
 * §2.7 の「上に伸ばすほど下の動線が苦しくなる」を目で確かめるための道具（§6）。
 */
export function drawTraffic(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  traffic: Map<NodeKey, number>,
): void {
  let max = 0;
  for (const v of traffic.values()) if (v > max) max = v;
  if (max <= 0) return;
  for (const [k, v] of traffic) {
    const u = v / max;
    const p = center(state, k);
    ctx.fillStyle = `rgba(240,${Math.round(192 - 130 * u)},${Math.round(105 - 60 * u)},${0.12 + u * 0.5})`;
    const h = 2 + u * 9;
    ctx.fillRect(p.x - 4, p.y + 2 - h, 8, h);
  }
}

/** 選択した猫の経路と目的地（§6「なぜその経路を選んだか」） */
export function drawCatPath(
  ctx: CanvasRenderingContext2D,
  state: GameState,
  cat: CatState,
): void {
  if (cat.path && cat.path.length > 1) {
    ctx.strokeStyle = 'rgba(240,192,105,.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    cat.path.forEach((k, i) => {
      const p = center(state, k);
      if (i === 0) ctx.moveTo(p.x, p.y);
      else ctx.lineTo(p.x, p.y);
    });
    ctx.stroke();
  }
  if (cat.goal !== null) {
    const g = center(state, cat.goal);
    ctx.strokeStyle = 'rgba(240,192,105,.95)';
    ctx.lineWidth = 1.5;
    ctx.strokeRect(g.x - 5, g.y - 5, 10, 10);
  }
}

/** 開通した瞬間の光（§3.2 開通の感触） */
export interface Flash {
  c: number;
  r: number;
  t: number;
}

export function drawFlashes(ctx: CanvasRenderingContext2D, flashes: Flash[], dt: number): Flash[] {
  const alive: Flash[] = [];
  for (const f of flashes) {
    f.t += dt;
    const u = f.t / 0.85;
    if (u >= 1) continue;
    ctx.strokeStyle = `rgba(95,211,189,${(1 - u) * 0.9})`;
    ctx.lineWidth = 2;
    const g = (1 - u) * 10;
    ctx.strokeRect(f.c * TILE + TILE / 2 - g / 2, (f.r + 1) * TILE - 2 - g / 2, g, g);
    alive.push(f);
  }
  return alive;
}

/**
 * 到着した猫の来歴を一行だけ、その猫のそばに出す（§1 / §3.6）。
 * 通知欄もログも作らない。読み逃しても構わない。
 */
export function drawHistoryLine(
  ctx: CanvasRenderingContext2D,
  cat: CatState,
  fade: number,
): void {
  if (!cat.history) return;
  const alpha = Math.max(0, Math.min(1, fade));
  if (alpha <= 0) return;

  ctx.save();
  ctx.font = '8px "Hiragino Sans","Noto Sans JP",system-ui,sans-serif';
  ctx.textAlign = 'center';
  const w = ctx.measureText(cat.history).width + 10;
  const x = cat.x;
  const y = cat.y - 30;
  ctx.fillStyle = `rgba(14,17,22,${(alpha * 0.75).toFixed(3)})`;
  ctx.fillRect(x - w / 2, y - 9, w, 12);
  ctx.fillStyle = `rgba(195,204,214,${alpha.toFixed(3)})`;
  ctx.fillText(cat.history, x, y);
  ctx.restore();
}

/** 指の下のマスを示す */
export function drawCursor(
  ctx: CanvasRenderingContext2D,
  c: number,
  r: number,
  ok: boolean,
): void {
  ctx.strokeStyle = ok ? 'rgba(95,211,189,.7)' : 'rgba(240,105,105,.5)';
  ctx.lineWidth = 1;
  ctx.strokeRect(c * TILE + 0.5, r * TILE + 0.5, TILE - 1, TILE - 1);
}
